<template>
  <v-container>
    <div class="d-flex flex-wrap justify-between align-center" style="gap: 10px">
      <h1>Account List</h1>

      <!-- Add Account Button (on the right side) -->
      <div
        class="d-flex justify-end"
        :class="$vuetify.display.mdAndDown ? 'flex-wrap' : 'ga-3'"
        style="gap: 0px; flex: 1"
      >
        <div
          v-if="userRole !== 'company_user'"
          :style="$vuetify.display.mdAndDown ? 'max-width:100%' : 'max-width:300px'"
          style="width: 100%; text-align: right"
        >
          <v-btn
            to="/admin/add-account"
            variant="text"
            size="small"
            prepend-icon="mdi-plus"
            class="text-grey-darken-2"
            data-testid="add-account-btn"
          >
            Add new account
          </v-btn>
        </div>
      </div>
    </div>

    <p class="my-6 text-body-2 text-grey-darken-1">
      Click on an account row to view details and manage account settings.
    </p>

    <!-- Keep the table visible and use the built-in loading overlay -->
    <div>
      <v-card>
        <v-toolbar color="primary" density="comfortable" flat>
          <!-- Filter Menu Button -->
          <v-menu offset-y v-model="filterMenuOpen" :close-on-content-click="false">
            <template v-slot:activator="{ props }">
              <v-btn
                data-testid="filter-menu-btn"
                v-bind="props"
                variant="text"
                class="text-white ml-2"
                prepend-icon="mdi-filter-variant"
              >
                Filter Status
                <v-badge
                  v-if="selectedStatuses.length < 2"
                  :content="selectedStatuses.length"
                  color="error"
                  inline
                  class="ml-2"
                />
              </v-btn>
            </template>
            <v-list density="compact">
              <v-list-item>
                <v-checkbox
                  data-testid="filter-active"
                  v-model="tempSelectedStatuses"
                  value="active"
                  density="compact"
                  hide-details
                  :label="`Active (${activeCount})`"
                />
              </v-list-item>
              <v-list-item>
                <v-checkbox
                  data-testid="filter-inactive"
                  v-model="tempSelectedStatuses"
                  value="inactive"
                  density="compact"
                  hide-details
                  :label="`Inactive (${inactiveCount})`"
                />
              </v-list-item>
              <v-divider class="my-2"></v-divider>
              <v-list-item>
                <div class="d-flex ga-2" style="width: 100%">
                  <v-btn
                    data-testid="filter-check-all-btn"
                    size="small"
                    variant="text"
                    @click="checkAllFilters"
                  >
                    Check All
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn
                    data-testid="filter-apply-btn"
                    size="small"
                    color="primary"
                    variant="flat"
                    @click="applyFilters"
                  >
                    Apply
                  </v-btn>
                </div>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-spacer></v-spacer>
          <!-- Desktop search (compact, inline) -->
          <v-text-field
            v-if="!$vuetify.display.mdAndDown"
            ref="searchField"
            v-model="searchQuery"
            placeholder="Search by name or JUID"
            clearable
            prepend-inner-icon="mdi-magnify"
            hide-details
            density="compact"
            variant="solo"
            class="search-in-bar mr-2"
            data-testid="search-input"
          ></v-text-field>
        </v-toolbar>
        <!-- Mobile search (full width on its own row) -->
        <div class="px-2 pt-2 mb-2" v-if="$vuetify.display.mdAndDown">
          <v-text-field
            ref="searchField"
            v-model="searchQuery"
            placeholder="Search by name or JUID"
            clearable
            prepend-inner-icon="mdi-magnify"
            hide-details
            density="comfortable"
            variant="solo"
            style="width: 100%; max-width: 100%"
          ></v-text-field>
        </div>
      </v-card>

      <div class="overflow-x-auto">
        <v-data-table-server
          :key="'account-table-' + itemsLength"
          item-value="juid"
          :headers="headers"
          :items="filteredAccounts"
          :loading="isLoading"
          class="elevation-1 sticky-header striped-rows even-cols"
          :style="{ '--even-col-count': headers.length }"
          @click:row="(evt, row) => onRowClick(row)"
          dense
          :item-class="getRowClass"
          :items-per-page="itemsPerPage"
          v-model:page="currentPage"
          :items-length="itemsLength"
          @update:items-per-page="onItemsPerPageChange"
          @update:page="onPageChange"
          data-testid="accounts-table"
        >
          <template #loading>
            <div class="height-loader"></div>
          </template>
          <template #no-data>
            <div class="text-center py-5">No data available</div>
          </template>
          <template #item.is_active="{ item }">
            <div class="d-flex justify-start" style="width: 100%">
              <v-chip size="x-small" :color="item.is_active ? 'green' : 'red'" variant="tonal">
                {{ item.is_active ? 'Active' : 'Inactive' }}
              </v-chip>
            </div>
          </template>
        </v-data-table-server>
      </div>
    </div>
    <account-add-product-modal
      v-if="showAddProductModal"
      :account-name="selectedAccountName"
      :show="showAddProductModal"
      @close="closeAddProductModal"
    />
    <account-products-modal
      v-if="selectedAccount"
      :account="selectedAccount"
      :show="showProductsModal"
      @close="closeProductsModal"
    />
    <v-dialog v-model="dialog" max-width="400">
      <v-card>
        <v-card-title>Confirm Action</v-card-title>
        <v-card-text>
          Are you sure you want to {{ selectedAccount.enabled ? 'enable' : 'disable' }} this
          account?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="dialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="toggleAccountFunc">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { fetchAccounts, toggleAccountStatus, VerifyAdmin } from '@/api/accountsAdmin'
import { useRouter } from 'vue-router'
import ProgressLoader from './ProgressLoader.vue'
// removed client list
import { useClientNameStore } from '@/stores/clinetName'
const { clientSelect } = useClientNameStore()
import { useUserStore } from '@/stores/userStore'
import { useAccountStore } from '@/stores/accountStore'

import AccountProductsModal from '@/components/AccountProductsModal.vue'
import AccountAddProductModal from '@/components/AccountAddProductModal.vue'
const showProductsModal = ref(false)

const showAddProductModal = ref(false)
const selectedAccountName = ref('')

const openAddProductModal = (accountName) => {
  selectedAccountName.value = accountName
  showAddProductModal.value = true
}

const closeAddProductModal = () => {
  showAddProductModal.value = false
}

const openProductsModal = (account) => {
  selectedAccount.value = account
  showProductsModal.value = true
  router.push({ query: { juid: account.juid } })
}

const closeProductsModal = () => {
  showProductsModal.value = false
  selectedAccount.value = null
  router.push({ query: {} })
}
const userStore = useUserStore()
console.log(userStore)

const accounts = ref([])
const accountStore = useAccountStore()
const router = useRouter()
const dialog = ref(false)
const selectedAccount = ref(null)
const newStatus = ref(false)
const isLoading = ref(true) // Track API loading state
const tab = ref('all')
// Status checkbox state (default both selected)
const selectedStatuses = ref(['active', 'inactive'])
const tempSelectedStatuses = ref(['active', 'inactive'])
const filterMenuOpen = ref(false)
// removed client list
const query = ref([])
const searchQuery = ref('')
const searchField = ref(null)
const sortOrder = ref('asc')
const snackbar = ref({
  show: false,
  message: '',
  color: 'info',
})

// Track the most recent load to ignore stale responses
let latestLoadSeq = 0

// Server-side pagination state
const totalAccounts = ref(0)
const itemsPerPage = ref(50)
const currentPage = ref(1)
const nextToken = ref(null)
let searchDebounceTimer = null

const onRowClick = (item) => {
  console.log(item.item.folder)
  router.push(`/account-admin/${item?.item?.folder}`)
}
const userRole = ref(userStore?.role)

function slugifySpaces(s) {
  return s
    .trim() // remove leading/trailing spaces
    .replace(/\s+/g, '-') // collapse all whitespace runs into ‘-’
}
const getRowClass = (item) => {
  return item.is_active === false || 'false' || null || '' ? 'inactive-row' : ''
}

function goCreateUser(item) {
  // 1) stash the friendly name
  localStorage.setItem('accountNameFriendly', item.name || '')
  // 2) then navigate
  router.push({
    path: '/users/create-user',
    query: {
      juid: item.juid,
      'create-client-admin': true,
    },
  })
}

// watch(
//   query,
//   async (newQuery) => {
//     clientSelect.clientName = newQuery;
//     await loadAccounts(newQuery); // No need for special "all" handling
//   },
//   { immediate: true }
// );
console.log(query.value)
console.log(clientSelect.clientName)

const headers = [
  {
    title: 'Account Name',
    value: 'name',
    sortable: false,
  },
  {
    title: 'JUID',
    value: 'juid',
    sortable: false,
  },
  {
    title: 'Status',
    value: 'is_active',
    sortable: false,
    align: 'start',
  },
]

// removed client list


const storeObjInLocal = (item) => {
  localStorage.setItem('accountObj', item)
}

const loadAccounts = async (options = {}) => {
  const seq = ++latestLoadSeq
  isLoading.value = true
  try {
    // Determine which status filter to apply based on selected checkboxes
    let statusFilter = 'all'
    const statuses = new Set(selectedStatuses.value)
    if (statuses.has('active') && !statuses.has('inactive')) {
      statusFilter = 'active'
    } else if (!statuses.has('active') && statuses.has('inactive')) {
      statusFilter = 'inactive'
    }

    const response = await fetchAccounts({
      limit: itemsPerPage.value,
      search: searchQuery.value || undefined,
      status: statusFilter,
      nextToken: computeNextToken(),
      ...options,
    })

    // Ignore this response if a newer request started while we were waiting
    if (seq !== latestLoadSeq) return

    // Handle new paginated response structure
    if (response.accounts) {
      const mapped = response.accounts.map((account) => ({
        name: account.accountNameFriendly || account.folderName || 'No Name',
        folder: account?.folderName,
        juid: account.juid,
        a5Account: account.a5Account,
        is_active: account.is_active === undefined ? null : account.is_active,
      }))
      accounts.value = mapped
      const totalFromServer = Number(
        response.total ?? (response.totalActive ?? 0) + (response.totalInactive ?? 0)
      )
      totalAccounts.value = totalFromServer > 0 ? totalFromServer : mapped.length
      serverTotals.value.totalActive = response.totalActive ?? null
      serverTotals.value.totalInactive = response.totalInactive ?? null
      // We rely on nextToken paging by index; Vuetify will call page changes which we map
      // through computeNextToken()
    } else {
      // Fallback for old response format (if backend not updated yet)
      const sortedData = [...response].sort((a, b) => {
        const nameA = (a.accountNameFriendly || a.folderName || 'No Name').toUpperCase()
        const nameB = (b.accountNameFriendly || b.folderName || 'No Name').toUpperCase()
        return nameA.localeCompare(nameB)
      })

      const mapped = sortedData.map((account) => ({
        name: account.accountNameFriendly || account.folderName || 'No Name',
        folder: account?.folderName,
        juid: account.juid,
        a5Account: account.a5Account,
        is_active: account.is_active === undefined ? null : account.is_active,
      }))
      accounts.value = mapped
      totalAccounts.value = mapped.length
    }
  } catch (error) {
    if (seq === latestLoadSeq) {
      console.error('Failed to load accounts:', error)
    }
  } finally {
    if (seq === latestLoadSeq) {
      isLoading.value = false
    }
  }
}

// With server-side filtering and pagination, we just return the accounts as-is
const filteredAccounts = computed(() => {
  return accounts.value
})

// New computed properties for counts that update with search

const sortedAccounts = computed(() => {
  return filteredAccounts.value // Just return filtered accounts without sorting
})
// removed client list

onMounted(() => {
  VerifyAdmin()
  loadAccounts()
})

// Debounced search - reload when user stops typing for 500ms
watch(searchQuery, (newVal, oldVal) => {
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer)
  }
  searchDebounceTimer = setTimeout(() => {
    currentPage.value = 1 // Reset to first page on new search
    loadAccounts()
  }, 500)
})

// Reload when status filters change
watch(selectedStatuses, () => {
  currentPage.value = 1 // Reset to first page on filter change
  loadAccounts()
})

watch(isLoading, (val, oldVal) => {
  if (oldVal && !val) {
    setTimeout(() => {
      try {
        if (searchField && searchField.value && typeof searchField.value.focus === 'function') {
          searchField.value.focus()
        }
      } catch (e) {}
    }, 0)
  }
})

const navigateToAccountProducts = (account) => {
  router.push(`/account/${account.folder}`)
}

const navigateToEditAccount = (account) => {
  router.push(`/account-admin/edit/${account.folder}`)
}

const addProductForAccount = (account) => {
  router.push(`/account/products/${account.folder}`)
}

const confirmToggleAccountStatus = (account, status) => {
  selectedAccount.value = { name: account.folder, enabled: status }
  newStatus.value = status
  dialog.value = true
}

const toggleAccountFunc = async () => {
  const accountName = selectedAccount.value.name
  console.log(accountName)
  const response = await toggleAccountStatus(accountName)
  console.log(response)

  snackbar.value = { show: true, message: 'Account Toggle Successful', color: 'success' }

  // Invalidate accounts cache so next visit fetches fresh data
  try {
    accountStore.setAccountsCache([])
  } catch {}

  // Reload accounts from server to get fresh data
  await loadAccounts()

  dialog.value = false
}

// const filteredAccounts = computed(() => {
//   if (tab.value === "active") {
//     return accounts.value.filter(account => account.is_active === true);
//   } else if (tab.value === "inactive") {
//     console.log("=======>", accounts.value.filter(account => account?.is_active === false))
//     return accounts.value.filter(account => !account?.is_active);
//   }
//   return accounts.value; // Default to all accounts
// });

// Compute counts for tabs - with server-side filtering, we use totalAccounts
const allCount = computed(() => {
  return totalAccounts.value
})

const activeCount = computed(() => {
  // Prefer server totals if provided
  if (serverTotals.value.totalActive != null) return serverTotals.value.totalActive
  return accounts.value.filter((a) => a.is_active).length
})

const inactiveCount = computed(() => {
  if (serverTotals.value.totalInactive != null) return serverTotals.value.totalInactive
  return accounts.value.filter((a) => !a.is_active).length
})

// Track server-reported totals (optional)
const serverTotals = ref({ totalActive: null, totalInactive: null })

// The table footer reads from this computed (avoids stale rerender keys)
const itemsLength = computed(() => Number(totalAccounts.value || 0))

// Compute the nextToken from currentPage and itemsPerPage (index offset)
function computeNextToken() {
  const page = Number(currentPage.value || 1)
  const size = Number(itemsPerPage.value || 50)
  const offset = (page - 1) * size
  return String(offset)
}

function onItemsPerPageChange(val) {
  itemsPerPage.value = Number(val || 50)
  currentPage.value = 1
  loadAccounts()
}

// Reload when the page changes
watch(currentPage, () => {
  loadAccounts()
})

function onPageChange(page) {
  currentPage.value = page || 1
  loadAccounts()
}

// Filter functions
function applyFilters() {
  selectedStatuses.value = [...tempSelectedStatuses.value]
  filterMenuOpen.value = false
}

function checkAllFilters() {
  tempSelectedStatuses.value = ['active', 'inactive']
}
</script>

<style scoped>
.height-loader {
  height: 50vh !important;
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-button {
  width: 25%;
  min-width: 150px;
}

.new-account-cta-row {
  /* padding-top: 15px; */
  margin: 0;
  display: flex;
  align-items: end;
  gap: 0px;
  border: 2px solid red;
}

.action-btn {
  margin-right: 10px;
}

.v-data-table tbody tr:hover {
  background-color: #f0f0f0;
}

:deep(.v-data-table tbody tr:hover) {
  background-color: #f0f0f0 !important;
}

.v-data-table {
  max-width: 800px;
  width: 100%;
  overflow-x: auto;
  min-width: 100%;
}

.list-styling {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.status-text {
  font-weight: 600;
}
.status-active {
  color: #2e7d32; /* green darken-3 */
}
.status-inactive {
  color: #c62828; /* red darken-3 */
}

.search-in-bar {
  max-width: 380px;
  opacity: 0.85;
  margin-right: 8px;
}
.search-in-bar:hover,
.search-in-bar:focus-within {
  opacity: 1;
}

/* Add this to your style section */
:deep(.v-data-table .inactive-row) {
  background-color: rgba(0, 0, 0, 0.774) !important;
}

:deep(.v-data-table .inactive-row > td) {
  color: #999 !important;
  opacity: 0.8;
}

:deep(.v-data-table .inactive-row:hover > td) {
  background-color: rgba(15, 4, 4, 0.887) !important;
}

/* Optional: Add "Inactive" indicator for mobile view */
:deep(.v-data-table .inactive-row .v-data-table__mobile-row:first-child::after) {
  content: ' (Inactive)';
  color: #f44336;
  font-size: 0.8em;
}

:deep(.v-data-table-header) {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: rgb(137, 44, 44) !important;
  /* Match your theme */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

:deep(.striped-rows .v-data-table__tr:nth-child(even)) {
  background-color: #f8f9fa;
  /* Very light gray - adjust to match your theme */
}

/* Hover effect */
:deep(.striped-rows .v-data-table__tr:hover) {
  background-color: #e9ecef !important;
  /* Slightly darker on hover */
}

/* Ensure text remains readable on striped rows */
:deep(.striped-rows .v-data-table__tr) {
  color: inherit;
  /* Inherits from parent */
}

.even-cols :deep(.v-data-table__wrapper table) {
  table-layout: fixed;
}

.even-cols :deep(th),
.even-cols :deep(td) {
  width: calc(100% / var(--even-col-count, 4));
}

@media (max-width: 600px) {
  .v-data-table {
    max-width: 1200px;
    width: 100%;
    overflow-x: auto;
    min-width: 1100px;
  }
}
</style>
